import { acceptHMRUpdate, defineStore } from "pinia";
import { markRaw, ref, type Component, type Ref } from "vue";

export type ModalType = "alert" | "expandable" | "confirm" | "prompt" | "multiline-prompt";

export interface BuiltinModal {
  kind: "builtin";
  title: string;
  message: string;
  type: ModalType;
  confirmText?: string;
  cancelText?: string;
  titleIcon?: string;
  promptedValueName?: string;
  response?: string;
  onConfirm: () => void;
  onCancel: () => void;
  inputType?: string;
  inputPattern?: string;
  initialValue?: string;
  error?: string;
  isDangerous?: boolean;
  body?: Component;
  bodyProps?: Record<string, unknown>;
  models?: Record<string, Ref<unknown>>;
  expandableBody?: Component;
  expandableBodyProps?: Record<string, unknown>;
  expandLabel?: string;
  collapseLabel?: string;
}

export interface ComponentModal {
  kind: "component";
  title: string;
  titleIcon?: string;
  body: Component;
  bodyProps?: Record<string, unknown>;
  models?: Record<string, Ref<unknown>>;
  footer?: Component;
  footerProps?: Record<string, unknown>;
  close: (result: unknown) => void;
  cancel: () => void;
}

export type Modal = BuiltinModal | ComponentModal;

export const useModalsStore = defineStore("modals", () => {
  const stack = ref<Modal[]>([]);

  /**
   * Show an alert modal.
   *
   * Usage:
   *
   * ```ts
   * if (await alert("title", "message")) {
   *   // do something
   * }
   * ```
   *
   * @param title The title of the modal.
   * @param message The message of the modal.
   * @param confirmText The text of the confirm button.
   * @returns A promise that resolves to true if the user confirms the modal, false otherwise.
   */
  async function alert({
    title,
    titleIcon = "",
    message,
    confirmText = "OK",
  }: {
    title: string;
    titleIcon?: string;
    message: string;
    confirmText?: string;
  }) {
    return new Promise<void>((resolve) => {
      function handleConfirm() {
        stack.value.shift();
        resolve();
      }

      stack.value.push({
        kind: "builtin",
        title,
        titleIcon,
        message,
        type: "alert",
        confirmText,
        onConfirm: () => handleConfirm(),
        onCancel: () => handleConfirm(),
      });
    });
  }

  /**
   * Show an expandable modal.
   *
   * Usage:
   *
   * ```ts
   * await expandable({
   *   title: "API key created",
   *   body: ApiKeyCopyModalBody,
   *   bodyProps: { apiKey },
   *   models: { hasCopied },
   *   expandableBody: ApiKeyUsageTipsModalBody,
   *   expandLabel: "How to use API keys",
   *   collapseLabel: "Hide usage tips",
   * });
   * ```
   *
   * @param title The title of the modal.
   * @param titleIcon An optional icon class shown next to the title.
   * @param message Optional HTML message when no `body` is provided.
   * @param body Optional Vue component rendered in the main content area.
   * @param bodyProps Optional extra props forwarded to `body`.
   * @param models Optional refs for `v-model` bindings forwarded to `body`.
   * @param expandableBody Vue component rendered inside the expandable section.
   * @param expandableBodyProps Optional extra props forwarded to `expandableBody`.
   * @param expandLabel Footer button label when the expandable section is collapsed.
   * @param collapseLabel Footer button label when the expandable section is expanded.
   * @returns A promise that resolves when the modal is closed.
   */
  async function expandable({
    title,
    titleIcon = "",
    message = "",
    body,
    bodyProps,
    models,
    expandableBody,
    expandableBodyProps,
    expandLabel,
    collapseLabel,
  }: {
    title: string;
    titleIcon?: string;
    message?: string;
    body?: Component;
    bodyProps?: Record<string, unknown>;
    models?: Record<string, Ref<unknown>>;
    expandableBody: Component;
    expandableBodyProps?: Record<string, unknown>;
    expandLabel: string;
    collapseLabel: string;
  }) {
    return new Promise<void>((resolve) => {
      function handleClose() {
        stack.value.shift();
        resolve();
      }

      stack.value.push({
        kind: "builtin",
        title,
        titleIcon,
        message,
        type: "expandable",
        body: body ? markRaw(body) : undefined,
        bodyProps,
        models: models ? markRaw(models) : undefined,
        expandableBody: markRaw(expandableBody),
        expandableBodyProps,
        expandLabel,
        collapseLabel,
        onConfirm: () => handleClose(),
        onCancel: () => handleClose(),
      });
    });
  }

  /**
   * Show a confirm modal.
   *
   * Usage:
   *
   * ```ts
   * if (await confirm("title", "message")) {
   *   // do something
   * }
   * ```
   *
   * @param title The title of the modal.
   * @param message The message of the modal.
   * @param confirmText The text of the confirm button.
   * @param cancelText The text of the cancel button.
   * @returns A promise that resolves to true if the user confirms the modal, false otherwise.
   */
  async function confirm({
    title,
    titleIcon = "",
    message,
    confirmText = "OK",
    cancelText = "Cancel",
    isDangerous = false,
  }: {
    title: string;
    titleIcon?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDangerous?: boolean;
  }) {
    return new Promise<boolean>((resolve) => {
      function handleConfirm(isConfirmed: boolean) {
        stack.value.shift();
        resolve(isConfirmed);
      }

      stack.value.push({
        kind: "builtin",
        title,
        titleIcon,
        message,
        type: "confirm",
        confirmText,
        cancelText,
        onConfirm: () => handleConfirm(true),
        onCancel: () => handleConfirm(false),
        isDangerous,
      });
    });
  }

  /**
   * Show a prompt modal.
   *
   * Usage:
   *
   * ```ts
   * const response = await prompt("title", "message", "promptedValueName");
   * if (response) {
   *   // do something
   * }
   * ```
   *
   * @param title The title of the modal.
   * @param message The message of the modal.
   * @param promptedValueName The name of the prompted value.
   * @param confirmText The text of the confirm button.
   * @param cancelText The text of the cancel button.
   * @returns A promise that resolves to the prompted value.
   */
  async function prompt({
    title,
    titleIcon = "",
    message = "",
    promptedValueName = "",
    confirmText = "OK",
    cancelText = "Cancel",
    inputType = undefined,
    inputPattern = undefined,
    initialValue = "",
    error = "",
    isDangerous = false,
  }: {
    title: string;
    titleIcon?: string;
    message?: string;
    promptedValueName?: string;
    confirmText?: string;
    cancelText?: string;
    inputType?: string;
    inputPattern?: string;
    initialValue?: string;
    error?: string;
    isDangerous?: boolean;
  }) {
    return _prompt(
      title,
      titleIcon,
      message,
      promptedValueName,
      confirmText,
      cancelText,
      false,
      inputType,
      inputPattern,
      isDangerous,
      initialValue,
      error
    );
  }

  /**
   * Show a prompt modal.
   *
   * Usage:
   *
   * ```ts
   * const response = await multilinePrompt("title", "message", "promptedValueName");
   * if (response) {
   *   // do something
   * }
   * ```
   *
   * @param title The title of the modal.
   * @param message The message of the modal.
   * @param promptedValueName The name of the prompted value.
   * @param confirmText The text of the confirm button.
   * @param cancelText The text of the cancel button.
   * @returns A promise that resolves to the prompted value.
   */
  async function multilinePrompt({
    title,
    titleIcon = "",
    message = "",
    promptedValueName = "",
    confirmText = "OK",
    cancelText = "Cancel",
  }: {
    title: string;
    titleIcon?: string;
    message?: string;
    promptedValueName?: string;
    confirmText?: string;
    cancelText?: string;
  }) {
    return _prompt(title, titleIcon, message, promptedValueName, confirmText, cancelText, true);
  }

  function _prompt(
    title: string,
    titleIcon: string,
    message: string,
    promptedValueName: string,
    confirmText: string,
    cancelText: string,
    isMultiline: boolean,
    inputType?: string,
    inputPattern?: string,
    isDangerous?: boolean,
    initialValue = "",
    error = ""
  ) {
    return new Promise<[boolean, string]>((resolve) => {
      function handleConfirm(confirmed: boolean) {
        const m = stack.value.shift();
        resolve([confirmed, m?.kind === "builtin" ? (m.response ?? "") : ""]);
      }

      stack.value.push({
        kind: "builtin",
        title,
        titleIcon,
        message,
        type: isMultiline ? "multiline-prompt" : "prompt",
        promptedValueName,
        confirmText,
        cancelText,
        onConfirm: () => handleConfirm(true),
        onCancel: () => handleConfirm(false),
        inputType,
        inputPattern,
        initialValue,
        error,
        isDangerous,
      });
    });
  }

  /**
   * Open a component-backed modal rendered inside the global `ModalDialog` shell.
   *
   * Usage:
   *
   * ```ts
   * import MyModalBody from "@/components/modals/MyModalBody.vue";
   * import MyModalFooter from "@/components/modals/MyModalFooter.vue";
   * import { ref } from "vue";
   *
   * const name = ref("");
   * const result = await openComponentModal<string>({
   *   title: "Rename",
   *   body: MyModalBody,
   *   footer: MyModalFooter,
   *   models: { name }, // modal content can use defineModel("name")
   * });
   * ```
   *
   * Notes:
   * - `close(result)` and `cancel()` are injected as props into both `body` and `footer`.
   * - Prefer `models` (refs) for editable state so modal content can use `defineModel(...)`
   *   rather than mutating props.
   *
   * @param title The title of the modal.
   * @param titleIcon An optional icon class shown next to the title.
   * @param body The component rendered in the modal content area.
   * @param bodyProps Optional extra props forwarded to the body component.
   * @param models Optional `v-model` bindings (refs) forwarded to body/footer.
   * @param footer Optional component rendered in the modal footer/actions area.
   * @param footerProps Optional extra props forwarded to the footer component.
   * @returns A promise that resolves with the value passed to `close(result)`, or `undefined` if
   * the modal is cancelled (Escape / close button / `cancel()`).
   */
  async function openComponentModal<TResult = unknown>({
    title,
    titleIcon = "",
    body,
    bodyProps,
    models,
    footer,
    footerProps,
  }: {
    title: string;
    titleIcon?: string;
    body: Component;
    bodyProps?: Record<string, unknown>;
    models?: Record<string, Ref<unknown>>;
    footer?: Component;
    footerProps?: Record<string, unknown>;
  }) {
    return new Promise<[boolean, TResult | undefined]>((resolve) => {
      function close(result: unknown) {
        stack.value.shift();
        resolve([true, result as TResult]);
      }

      function cancel() {
        stack.value.shift();
        resolve([false, undefined]);
      }

      stack.value.push({
        kind: "component",
        title,
        titleIcon,
        body: markRaw(body),
        bodyProps,
        models: models ? markRaw(models) : undefined,
        footer: footer ? markRaw(footer) : undefined,
        footerProps,
        close,
        cancel,
      });
    });
  }

  /**
   * Get the current modal in the stack.
   * @returns The current modal.
   */
  function getCurrentModal() {
    return stack.value[0];
  }

  return {
    alert,
    expandable,
    confirm,
    prompt,
    multilinePrompt,
    openComponentModal,
    getCurrentModal,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useModalsStore, import.meta.hot));
}
